import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, UserPlus, Trash2, KeyRound, LogOut, Video, ExternalLink, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MeetingsList } from '../components/MeetingsList';
import { formatApiErrorDetail, useAuth } from '../context/AuthContext';
import { ADMIN, HOME } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ username: '', name: '', password: '', email: '' });
  const [creating, setCreating] = useState(false);
  const [trtcSettings, setTrtcSettings] = useState(null);

  const fetchUsers = () => {
    axios
      .get(`${API}/admin/users`, { withCredentials: true })
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => setUsers([]));
  };

  const fetchTrtcSettings = () => {
    axios
      .get(`${API}/admin/trtc-settings`, { withCredentials: true })
      .then(({ data }) => setTrtcSettings(data))
      .catch(() => setTrtcSettings(false));
  };

  useEffect(() => {
    fetchUsers();
    fetchTrtcSettings();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, { ...form, email: form.email.trim() || null }, { withCredentials: true });
      toast.success('用户已创建');
      setForm({ username: '', name: '', password: '', email: '' });
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/users/${id}`, { withCredentials: true });
      setUsers((old) => old.filter((u) => u.id !== id));
      toast.success('用户已删除');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleResetPassword = async (id) => {
    const newPassword = window.prompt('输入新密码（至少6位）：');
    if (!newPassword) return;
    try {
      await axios.patch(`${API}/admin/users/${id}/reset-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success('密码已重置');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleEditEmail = async (id, currentEmail) => {
    const newEmail = window.prompt('输入邮箱地址（留空可清除）：', currentEmail || '');
    if (newEmail === null) return;
    try {
      await axios.patch(`${API}/admin/users/${id}/email`, { email: newEmail.trim() || null }, { withCredentials: true });
      toast.success('邮箱已更新');
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid={ADMIN.container} className="min-h-screen bg-[#F8FAFC] px-4 py-6 sm:px-16 sm:py-10">
      <div className="mx-auto max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 rounded-full py-2 font-body text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> 返回首页
        </button>
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-slate-900 sm:text-3xl">管理员后台</h1>
          <button
            data-testid={HOME.navLogoutBtn}
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="flex items-center gap-1 rounded-full py-2 font-body text-sm text-slate-500 hover:text-slate-900"
          >
            <LogOut className="h-4 w-4" /> 退出
          </button>
        </div>

        <div className="mb-6 flex gap-2">
          <button
            data-testid={ADMIN.tabUsers}
            onClick={() => setTab('users')}
            className={`flex-1 rounded-full px-4 py-2.5 font-body text-sm sm:flex-none ${tab === 'users' ? 'bg-[#3B82F6] text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
          >
            用户管理
          </button>
          <button
            data-testid={ADMIN.tabMeetings}
            onClick={() => setTab('meetings')}
            className={`flex-1 rounded-full px-4 py-2.5 font-body text-sm sm:flex-none ${tab === 'meetings' ? 'bg-[#3B82F6] text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
          >
            全部会议记录
          </button>
          <button
            data-testid={ADMIN.tabTrtc}
            onClick={() => setTab('trtc')}
            className={`flex-1 rounded-full px-4 py-2.5 font-body text-sm sm:flex-none ${tab === 'trtc' ? 'bg-[#3B82F6] text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
          >
            腾讯云 TRTC 设置
          </button>
        </div>

        {tab === 'users' ? (
          <div>
            <form onSubmit={handleCreate} className="mb-6 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:flex-wrap sm:items-end">
              <div className="w-full sm:w-40">
                <label className="mb-1 block font-body text-xs text-slate-500">用户名</label>
                <Input
                  data-testid={ADMIN.newUsernameInput}
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="h-11 w-full border-slate-200 bg-slate-50"
                  required
                />
              </div>
              <div className="w-full sm:w-40">
                <label className="mb-1 block font-body text-xs text-slate-500">姓名</label>
                <Input
                  data-testid={ADMIN.newNameInput}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-11 w-full border-slate-200 bg-slate-50"
                  required
                />
              </div>
              <div className="w-full sm:w-40">
                <label className="mb-1 block font-body text-xs text-slate-500">密码</label>
                <Input
                  data-testid={ADMIN.newPasswordInput}
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-11 w-full border-slate-200 bg-slate-50"
                  required
                  minLength={6}
                />
              </div>
              <div className="w-full sm:w-48">
                <label className="mb-1 block font-body text-xs text-slate-500">邮箱（选填）</label>
                <Input
                  data-testid={ADMIN.newEmailInput}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="user@example.com"
                  className="h-11 w-full border-slate-200 bg-slate-50"
                />
              </div>
              <Button
                type="submit"
                data-testid={ADMIN.newUserSubmitBtn}
                disabled={creating}
                className="h-11 w-full rounded-full bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90 sm:w-auto"
              >
                <UserPlus className="mr-2 h-4 w-4" /> 创建用户
              </Button>
            </form>

            <div className="space-y-2">
              {users === null && <p className="font-body text-sm text-slate-400">加载中…</p>}
              {users?.map((u) => (
                <div
                  key={u.id}
                  data-testid={ADMIN.userRow(u.id)}
                  className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-body text-sm font-medium text-slate-900">
                      {u.name} <span className="ml-2 font-mono text-xs text-slate-400">@{u.username}</span>
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className={`inline-block rounded-full px-2 py-0.5 font-mono text-xs ${u.role === 'admin' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                        {u.role === 'admin' ? '管理员' : '普通用户'}
                      </span>
                      <span data-testid={ADMIN.userEmail(u.id)} className="font-mono text-xs text-slate-400">
                        {u.email || '未设置邮箱'}
                      </span>
                    </div>
                  </div>
                  {u.role !== 'admin' && (
                    <div className="flex gap-2 sm:justify-end">
                      <Button
                        data-testid={ADMIN.editEmailBtn(u.id)}
                        variant="outline"
                        size="icon"
                        onClick={() => handleEditEmail(u.id, u.email)}
                        className="h-10 w-10 rounded-full border-slate-200 text-slate-600 hover:bg-slate-100"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={ADMIN.resetPasswordBtn(u.id)}
                        variant="outline"
                        size="icon"
                        onClick={() => handleResetPassword(u.id)}
                        className="h-10 w-10 rounded-full border-slate-200 text-slate-600 hover:bg-slate-100"
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={ADMIN.deleteUserBtn(u.id)}
                        variant="outline"
                        size="icon"
                        onClick={() => handleDelete(u.id)}
                        className="h-10 w-10 rounded-full border-slate-200 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : tab === 'meetings' ? (
          <MeetingsList />
        ) : (
          <div data-testid={ADMIN.trtcContainer} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">
                <Video className="h-5 w-5" />
              </div>
              <div>
                <p className="font-heading text-base font-semibold text-slate-900">腾讯云视频通话（TRTC）连接</p>
                <p className="font-body text-xs text-slate-400">当前连接信息只读展示，如需修改请前往服务器 backend/.env 文件更新后重启服务</p>
              </div>
            </div>

            {trtcSettings === null && <p className="font-body text-sm text-slate-400">加载中…</p>}
            {trtcSettings === false && <p className="font-body text-sm text-red-500">获取配置失败，请稍后重试</p>}
            {trtcSettings && (
              <div className="space-y-4">
                <div
                  data-testid={ADMIN.trtcStatusBadge}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-xs ${
                    trtcSettings.configured ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
                  }`}
                >
                  {trtcSettings.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  {trtcSettings.configured ? '已连接' : '未配置'}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block font-body text-xs text-slate-500">SDKAppID</label>
                    <p data-testid={ADMIN.trtcSdkAppId} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-900">
                      {trtcSettings.sdk_app_id || '未设置'}
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block font-body text-xs text-slate-500">密钥（SecretKey）</label>
                    <p data-testid={ADMIN.trtcSecretMasked} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-900">
                      {trtcSettings.secret_key_masked || '未设置'}
                    </p>
                  </div>
                </div>

                <a
                  data-testid={ADMIN.trtcConsoleLink}
                  href={trtcSettings.console_url || 'https://console.trtc.io/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#3B82F6] px-4 py-2.5 font-body text-sm text-white hover:bg-[#3B82F6]/90"
                >
                  前往腾讯云 TRTC 控制台 <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
